import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BusinessClock from '../components/BusinessClock';
import { toast } from 'sonner';
import { formatBusinessDateTime } from '../lib/dateTime';
import { 
  Search, Plus, Minus, Trash2, CreditCard, Banknote, Smartphone, 
  ShoppingCart, User, Receipt, Loader2, AlertCircle, Wallet
} from 'lucide-react';

const POSPage = () => {
  const { api, businessSettings, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesEmployees, setSalesEmployees] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [walletAmount, setWalletAmount] = useState('');
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [selectedSalesEmployeeId, setSelectedSalesEmployeeId] = useState('none');
  const [lastReceipt, setLastReceipt] = useState(null);
  const [receiptDialog, setReceiptDialog] = useState(false);


  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes, customersRes] = await Promise.all([
        api.get('/products'),
        api.get('/products/categories'),
        api.get('/customers'),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setCustomers(customersRes.data);
      const employeesRes = await api.get('/employees/sales-options').catch(() => ({ data: [] }));
      setSalesEmployees(employeesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory && p.stock_quantity > 0;
  });

  const addToCart = (product) => {
    const existing = cart.find((item) => item.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock_quantity) {
        toast.error('Not enough stock');
        return;
      }
      setCart(cart.map((item) =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.selling_price,
        discount_amount: 0,
        tax_classification: product.tax_classification,
        stock_available: product.stock_quantity,
      }]);
    }
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map((item) => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.stock_available) {
          toast.error('Not enough stock');
          return item;
        }
        return {
          ...item,
          quantity: newQty,
          discount_amount: Math.min(item.discount_amount || 0, item.unit_price * newQty),
        };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedCustomer(null);
    setCouponCodeInput('');
    setAppliedCoupon(null);
  };

  // Calculate totals
  const grossSubtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const itemDiscountTotal = cart.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const subtotal = grossSubtotal - itemDiscountTotal;
  const couponDiscountAmount = Math.min(Math.max(Number(appliedCoupon?.discount_amount || 0), 0), subtotal);
  const vatRate = businessSettings?.tax_type === 'vat_registered' ? 18 : 0;
  const vatAmount = cart.reduce((sum, item) => {
    if (item.tax_classification === 'vatable' && vatRate > 0) {
      const itemTotal = item.unit_price * item.quantity - item.discount_amount;
      return sum + (itemTotal - itemTotal / 1.18);
    }
    return sum;
  }, 0);
  const totalAmount = Math.max(0, subtotal - couponDiscountAmount);
  const selectedCustomerWallet = Number(selectedCustomer?.wallet_balance || 0);
  const parsedWalletInput = parseFloat(walletAmount || '0') || 0;
  const rawWalletToUse = paymentMethod === 'wallet' ? totalAmount : parsedWalletInput;
  const walletToUse = Math.min(Math.max(rawWalletToUse, 0), totalAmount, selectedCustomerWallet);
  const remainingAfterWallet = Math.max(0, totalAmount - walletToUse);
  const changeAmount = (parseFloat(amountPaid) || 0) - remainingAfterWallet;

  const applyCoupon = async () => {
    const code = couponCodeInput.trim().toUpperCase();
    if (!code) {
      toast.error('Enter a coupon code');
      return;
    }
    try {
      const response = await backendApi.coupons.validate(api, { code, subtotal });
      const data = response.data;
      if (!data?.valid || !data?.coupon) {
        toast.error(data?.message || 'Invalid coupon code');
        return;
      }
      setAppliedCoupon({
        id: data.coupon.id,
        code: data.coupon.code,
        discount_type: data.coupon.discount_type,
        value: data.coupon.value,
        discount_amount: data.discount_amount,
      });
      toast.success(`Coupon applied: ${data.coupon.code}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to validate coupon');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput('');
    toast.success('Coupon removed');
  };

  useEffect(() => {
    const refreshCoupon = async () => {
      if (!appliedCoupon?.code || subtotal <= 0) return;
      try {
        const response = await backendApi.coupons.validate(api, { code: appliedCoupon.code, subtotal });
        const data = response.data;
        if (!data?.valid || !data?.coupon) {
          setAppliedCoupon(null);
          toast.error(data?.message || 'Applied coupon is no longer valid');
          return;
        }
        setAppliedCoupon({
          id: data.coupon.id,
          code: data.coupon.code,
          discount_type: data.coupon.discount_type,
          value: data.coupon.value,
          discount_amount: data.discount_amount,
        });
      } catch {
        setAppliedCoupon(null);
      }
    };

    refreshCoupon();
  }, [api, appliedCoupon?.code, subtotal]);

  const handlePayment = async () => {
    const walletRequested = walletToUse > 0;
    if (paymentMethod === 'credit' && !selectedCustomer) {
      toast.error('Credit sales require a customer');
      return;
    }
    if ((paymentMethod === 'wallet' || walletRequested) && !selectedCustomer) {
      toast.error('Wallet payment requires selecting a customer');
      return;
    }
    if (paymentMethod === 'wallet' && selectedCustomerWallet < totalAmount) {
      toast.error('Selected customer wallet balance is not enough for full wallet payment');
      return;
    }
    if (paymentMethod === 'credit' && walletRequested) {
      toast.error('Credit sale cannot be combined with wallet payment');
      return;
    }

    const paid = parseFloat(amountPaid) || 0;
    if (!['credit', 'wallet'].includes(paymentMethod) && paid < remainingAfterWallet) {
      toast.error('Insufficient payment amount');
      return;
    }

    setProcessingPayment(true);

    try {
      const saleData = {
        items: cart.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_amount: Math.min(Math.max(item.discount_amount || 0, 0), item.unit_price * item.quantity),
          tax_classification: item.tax_classification,
        })),
        customer_id: selectedCustomer?.id || null,
        payment_method: paymentMethod,
        amount_paid: paymentMethod === 'wallet' ? 0 : paid,
        wallet_amount: walletToUse,
        sales_employee_id: selectedSalesEmployeeId === 'none' ? null : selectedSalesEmployeeId,
        discount_percentage: appliedCoupon?.discount_type === 'percent' ? appliedCoupon.value : 0,
        discount_amount: appliedCoupon?.discount_type === 'amount' ? couponDiscountAmount : 0,
        notes: appliedCoupon ? `Coupon applied: ${appliedCoupon.code}` : undefined,
      };

      const response = await api.post('/sales', saleData);
      setLastReceipt(response.data);
      setPaymentDialog(false);
      setReceiptDialog(true);
      clearCart();
      setAmountPaid('');
      setWalletAmount('');
      setCouponCodeInput('');
      setAppliedCoupon(null);
      setSelectedSalesEmployeeId('none');
      fetchData(); // Refresh stock
      window.dispatchEvent(new Event('survipos:dashboard-refresh'));
      toast.success('Sale completed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process sale');
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatCurrency = (amount) => {
    return `UGX ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 grid grid-cols-1 gap-4 lg:h-full lg:grid-cols-12 lg:overflow-hidden" data-testid="pos-page">
      {/* Products Section */}
      <div className="lg:col-span-8 flex flex-col gap-4 min-h-0 lg:h-full">
        {/* Search and Filters */}
        <div className="flex flex-col xl:flex-row gap-4 shrink-0 xl:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search products by name, SKU, or barcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="pos-search-input"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48 h-12" data-testid="pos-category-select">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <BusinessClock className="self-start xl:self-auto" />
        </div>

        {/* Products Grid */}
        <ScrollArea className="min-h-0 lg:flex-1">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 pb-4">
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className="cursor-pointer hover:border-primary/50 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                onClick={() => addToCart(product)}
                data-testid={`product-card-${product.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={product.stock_quantity <= product.low_stock_threshold ? 'destructive' : 'secondary'} className="text-xs">
                      {product.stock_quantity} left
                    </Badge>
                    {product.tax_classification === 'vatable' && vatRate > 0 && (
                      <Badge variant="outline" className="text-xs">VAT</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{product.category}</p>
                  {product.description ? (
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {product.description}
                    </p>
                  ) : null}
                  <p className="text-lg font-mono font-bold text-primary">
                    {formatCurrency(product.selling_price)}
                  </p>
                </CardContent>
              </Card>
            ))}
            {filteredProducts.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No products found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Cart Section */}
      <div className="flex max-h-[calc(100vh-12rem)] flex-col overflow-hidden rounded-lg bg-card/88 shadow-lg lg:col-span-4 lg:h-full lg:max-h-full">
        <div className="p-4 shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Cart ({cart.length})
            </h2>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCart} data-testid="clear-cart-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Customer Selection */}
          <div className="mt-4">
            <Select
              value={selectedCustomer?.id || 'walk-in'}
              onValueChange={(value) => setSelectedCustomer(value === 'walk-in' ? null : customers.find((c) => c.id === value) || null)}
            >
              <SelectTrigger className="w-full" data-testid="customer-select">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <SelectValue placeholder="Walk-in Customer" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="walk-in">Walk-in Customer</SelectItem>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name} {customer.current_debt > 0 && `(Debt: ${formatCurrency(customer.current_debt)})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCustomer && selectedCustomer.current_debt > 0 && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Outstanding debt: {formatCurrency(selectedCustomer.current_debt)}
              </p>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Cart is empty</p>
              <p className="text-sm">Tap products to add them</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.product_id} className="flex flex-wrap items-center gap-3 p-3 bg-background/55 rounded-lg shadow-sm">
                  <div className="basis-full min-w-0">
                    <p className="font-medium text-sm break-words leading-tight">{item.product_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {formatCurrency(item.unit_price)} x {item.quantity}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.product_id, -1)}
                      data-testid={`qty-minus-${item.product_id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-mono font-bold">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.product_id, 1)}
                      data-testid={`qty-plus-${item.product_id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="font-mono font-bold text-sm basis-full text-right">
                    {formatCurrency(item.unit_price * item.quantity)}
                  </p>
                  <div className="basis-full flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => removeFromCart(item.product_id)}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </ScrollArea>

        {/* Totals and Pay Button */}
        {cart.length > 0 && (
          <div className="p-4 space-y-4 shrink-0 bg-card/95">
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Coupon Code</label>
              <div className="flex gap-2">
                <Input
                  value={couponCodeInput}
                  onChange={(e) => setCouponCodeInput(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="h-9 font-mono"
                />
                <Button variant="outline" className="h-9" onClick={applyCoupon}>
                  Apply
                </Button>
                {appliedCoupon && (
                  <Button variant="ghost" className="h-9" onClick={removeCoupon}>
                    Remove
                  </Button>
                )}
              </div>
              {appliedCoupon && (
                <p className="text-xs text-primary">
                  Active coupon: <span className="font-mono">{appliedCoupon.code}</span>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gross Subtotal</span>
                <span className="font-mono">{formatCurrency(grossSubtotal)}</span>
              </div>
              {itemDiscountTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Item Discounts</span>
                  <span className="font-mono text-warning">-{formatCurrency(itemDiscountTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-mono">{formatCurrency(subtotal)}</span>
              </div>
              {couponDiscountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Coupon Discount</span>
                  <span className="font-mono text-warning">-{formatCurrency(couponDiscountAmount)}</span>
                </div>
              )}
              {vatAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT (18%)</span>
                  <span className="font-mono">{formatCurrency(vatAmount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="font-mono text-primary">{formatCurrency(totalAmount)}</span>
              </div>
            </div>

            <Button
              className="w-full h-16 text-xl font-bold neon-green"
              onClick={() => {
                setAmountPaid(totalAmount.toString());
                setPaymentDialog(true);
              }}
              data-testid="pay-btn"
            >
              PAY {formatCurrency(totalAmount)}
            </Button>
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Complete Payment</DialogTitle>
            <DialogDescription>
              Total: <span className="font-mono font-bold text-primary">{formatCurrency(totalAmount)}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4 overflow-y-auto pr-1">
            {/* Payment Method Selection */}
            <div className="sticky top-0 z-10 bg-card/95 backdrop-blur pb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                  className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words"
                  onClick={() => setPaymentMethod('cash')}
                  data-testid="payment-cash"
                >
                  <Banknote className="h-6 w-6" />
                  <span>Cash</span>
                </Button>
                <Button
                  variant={paymentMethod === 'mtn_mobile_money' ? 'default' : 'outline'}
                  className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words"
                  onClick={() => setPaymentMethod('mtn_mobile_money')}
                  style={paymentMethod === 'mtn_mobile_money' ? { backgroundColor: '#FFCC00', color: '#000' } : {}}
                  data-testid="payment-mtn"
                >
                  <Smartphone className="h-6 w-6" />
                  <span>MTN MoMo</span>
                </Button>
                <Button
                  variant={paymentMethod === 'airtel_money' ? 'default' : 'outline'}
                  className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words"
                  onClick={() => setPaymentMethod('airtel_money')}
                  style={paymentMethod === 'airtel_money' ? { backgroundColor: '#FF0000' } : {}}
                  data-testid="payment-airtel"
                >
                  <Smartphone className="h-6 w-6" />
                  <span>Airtel Money</span>
                </Button>
                <Button
                  variant={paymentMethod === 'bank_card' ? 'default' : 'outline'}
                  className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words"
                  onClick={() => setPaymentMethod('bank_card')}
                  data-testid="payment-card"
                >
                  <CreditCard className="h-6 w-6" />
                  <span>Card</span>
                </Button>
                {selectedCustomer && (
                  <Button
                    variant={paymentMethod === 'wallet' ? 'default' : 'outline'}
                    className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words sm:col-span-2"
                    onClick={() => setPaymentMethod('wallet')}
                    data-testid="payment-wallet"
                  >
                    <Wallet className="h-6 w-6" />
                    <span>Wallet ({formatCurrency(selectedCustomer.wallet_balance || 0)})</span>
                  </Button>
                )}
                {selectedCustomer && (
                  <Button
                    variant={paymentMethod === 'credit' ? 'destructive' : 'outline'}
                    className="h-14 sm:h-16 flex-col gap-1 text-center whitespace-normal break-words sm:col-span-2"
                    onClick={() => setPaymentMethod('credit')}
                    data-testid="payment-credit"
                  >
                    <User className="h-6 w-6" />
                    <span>Credit Sale ({selectedCustomer.name})</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Amount Input */}
            {paymentMethod !== 'credit' && paymentMethod !== 'wallet' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Amount Received (UGX)</label>
                <Input
                  type="number"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  className="h-14 text-2xl font-mono text-center"
                  data-testid="amount-paid-input"
                />
                {parseFloat(amountPaid) >= remainingAfterWallet && (
                  <p className="text-center text-lg">
                    Change: <span className="font-mono font-bold text-primary">{formatCurrency(changeAmount)}</span>
                  </p>
                )}
              </div>
            )}

            {paymentMethod !== 'credit' && paymentMethod !== 'wallet' && selectedCustomer && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Wallet Contribution (optional) - Available {formatCurrency(selectedCustomerWallet)}
                </label>
                <Input
                  type="number"
                  value={walletAmount}
                  onChange={(e) => setWalletAmount(e.target.value)}
                  className="h-12 font-mono text-center"
                  data-testid="wallet-amount-input"
                />
                <p className="text-xs text-muted-foreground">
                  Remaining cash/card/momo required: {formatCurrency(remainingAfterWallet)}
                </p>
              </div>
            )}

            {paymentMethod === 'wallet' && selectedCustomer && (
              <div className="p-4 bg-background/55 rounded-lg shadow-sm">
                <p className="text-sm">Wallet balance: <span className="font-mono">{formatCurrency(selectedCustomerWallet)}</span></p>
                <p className="text-sm">Wallet used: <span className="font-mono">{formatCurrency(totalAmount)}</span></p>
              </div>
            )}

            {paymentMethod === 'credit' && selectedCustomer && (
              <div className="p-4 bg-destructive/10 rounded-lg">
                <p className="text-sm">
                  Credit limit: <span className="font-mono">{formatCurrency(selectedCustomer.credit_limit)}</span>
                </p>
                <p className="text-sm">
                  Current debt: <span className="font-mono">{formatCurrency(selectedCustomer.current_debt)}</span>
                </p>
                <p className="text-sm font-semibold">
                  Available credit: <span className="font-mono">{formatCurrency(selectedCustomer.credit_limit - selectedCustomer.current_debt)}</span>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Sales Employee (optional)</label>
              <Select value={selectedSalesEmployeeId} onValueChange={setSelectedSalesEmployeeId}>
                <SelectTrigger data-testid="sales-employee-select">
                  <SelectValue placeholder="No employee attribution" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No employee attribution</SelectItem>
                  {salesEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={
                processingPayment ||
                (
                  !['credit', 'wallet'].includes(paymentMethod) &&
                  (parseFloat(amountPaid) || 0) < remainingAfterWallet
                ) ||
                (paymentMethod === 'wallet' && (!selectedCustomer || selectedCustomerWallet < totalAmount))
              }
              className="neon-green"
              data-testid="confirm-payment-btn"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Payment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Receipt className="h-6 w-6 text-primary" />
              Receipt
            </DialogTitle>
          </DialogHeader>

          {lastReceipt && (
            <div className="space-y-4 py-4 font-mono text-sm">
              <div className="text-center border-b border-dashed pb-4">
                <h3 className="font-heading text-lg font-bold">{businessSettings?.business_name}</h3>
                {businessSettings?.tin_number && (
                  <p className="text-muted-foreground">TIN: {businessSettings.tin_number}</p>
                )}
                <p className="text-muted-foreground">{businessSettings?.address}</p>
              </div>

              <div className="space-y-1">
                <p>Receipt: <span className="font-bold">{lastReceipt.receipt_number}</span></p>
                <p>Date: {formatBusinessDateTime(lastReceipt.created_at)}</p>
                <p>Cashier: {lastReceipt.cashier_name}</p>
                {lastReceipt.customer_name && <p>Customer: {lastReceipt.customer_name}</p>}
              </div>

              <Separator className="border-dashed" />

              <div className="space-y-2">
                {lastReceipt.items.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="flex-1">{item.product_name} x{item.quantity}</span>
                    <span>{formatCurrency(item.unit_price * item.quantity - (item.discount_amount || 0))}</span>
                  </div>
                ))}
              </div>

              <Separator className="border-dashed" />

              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(lastReceipt.subtotal)}</span>
                </div>
                {lastReceipt.total_discount > 0 && (
                  <div className="flex justify-between">
                    <span>Discounts</span>
                    <span>-{formatCurrency(lastReceipt.total_discount)}</span>
                  </div>
                )}
                {lastReceipt.vat_amount > 0 && (
                  <div className="flex justify-between">
                    <span>VAT (18%)</span>
                    <span>{formatCurrency(lastReceipt.vat_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>TOTAL</span>
                  <span>{formatCurrency(lastReceipt.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Paid ({lastReceipt.payment_method.replace('_', ' ')})</span>
                  <span>{formatCurrency(lastReceipt.amount_paid)}</span>
                </div>
                {lastReceipt.wallet_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Wallet Used</span>
                    <span>{formatCurrency(lastReceipt.wallet_amount)}</span>
                  </div>
                )}
                {lastReceipt.change_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Change</span>
                    <span>{formatCurrency(lastReceipt.change_amount)}</span>
                  </div>
                )}
                {lastReceipt.is_credit_sale && (
                  <div className="flex justify-between text-destructive">
                    <span>Credit Amount</span>
                    <span>{formatCurrency(lastReceipt.credit_amount)}</span>
                  </div>
                )}
              </div>

              <div className="text-center text-xs text-muted-foreground pt-4 border-t border-dashed">
                <p>Powered by Kareem Data Labs</p>
                <p>URA-Compliant POS for Ugandan SMEs</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setReceiptDialog(false)} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPage;

